import { useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { useStoreDetails } from '@/entities/store/api'
import { useAuthStore } from '@/features/auth/model'
import { FORCE_UPDATE_SESSION_KEY } from '@/features/native-app/model'
import { useOrderDetailsNavigation } from '@/features/order/lib'
import { useSettingsStore } from '@/shared/model/settings.store'

/**
 * Returns a stable function to navigate to order details.
 */
export function useNativeHandlerOrderDetailsModal() {
  const navigateToOrderDetails = useOrderDetailsNavigation()

  const openOrderDetailsModal = useCallback((orderId?: string) => {
    if (!orderId) {
      console.warn('No orderId provided to openOrderDetailsModal')
      return false
    }
    navigateToOrderDetails(orderId)
    return true
  }, [navigateToOrderDetails])

  return {
    openOrderDetailsModal,
  }
}

/**
 * Returns a stable function to get store details.
 */
export function useNativeHandlerStoreDetails() {
  const { data: storeDetails } = useStoreDetails()

  const getStoreDetails = useCallback(() => storeDetails, [storeDetails])

  return {
    getStoreDetails,
  }
}

/**
 * Exposes sound control state and actions from the Zustand store.
 */
export function useNativeHandlerSoundControl() {
  const settingsStore = useSettingsStore()

  const getIsSoundEnabled = useCallback(() => {
    return settingsStore.isSoundEnabled
  }, [settingsStore.isSoundEnabled])

  /**
   * @description Enables notification sounds.
   * @returns {Promise<boolean>}
   * **Important Note for iOS Native Integration:**
   * When this function is called directly from native iOS code (e.g., via a WebView bridge),
   * the native environment is designed to immediately receive a synchronous `true` value.
   * This is a workaround because native iOS cannot directly "await" or handle JavaScript Promise
   * return types from such direct calls, preventing exceptions on the native side.
   *
   * In a pure JavaScript context (or when integrated via the React Native bridge on Android),
   * this function returns a `Promise<boolean>` that resolves to `true` upon the successful
   * initiation of the sound enablement process.
   */
  const enableNotificationSounds = useCallback(async () => {
    settingsStore.enableSound()
    return true // This 'true' is critical for the iOS native workaround.
  }, [settingsStore])

  /**
   * @description Disables notification sounds.
   * @returns {Promise<boolean>}
   * **Important Note for iOS Native Integration:**
   * When this function is called directly from native iOS code (e.g., via a WebView bridge),
   * the native environment is designed to immediately receive a synchronous `true` value.
   * This is a workaround because native iOS cannot directly "await" or handle JavaScript Promise
   * return types from such direct calls, preventing exceptions on the native side.
   *
   * In a pure JavaScript context (or when integrated via the React Native bridge on Android),
   * this function returns a `Promise<boolean>` that resolves to `true` upon the successful
   * initiation of the sound disablement process.
   */
  const disableNotificationSounds = useCallback(async () => {
    settingsStore.disableSound()
    return true // This 'true' is critical for the iOS native workaround.
  }, [settingsStore])

  return {
    getIsSoundEnabled,
    enableNotificationSounds,
    disableNotificationSounds,
  }
}

/**
 * Returns a stable function that navigates to the Force Update screen.
 * Stores the update payload in sessionStorage so the route guard can verify
 * the navigation came from the native bridge.
 */
export function useNativeHandlerForceRefresh() {
  const navigate = useNavigate()

  const forceRefresh = useCallback((updateUrl: string, installedVersion: string, minimumVersion: string) => {
    sessionStorage.setItem(
      FORCE_UPDATE_SESSION_KEY,
      JSON.stringify({ updateUrl, installedVersion, minimumVersion }),
    )
    navigate({ to: '/app-update', replace: true })
  }, [navigate])

  return {
    forceRefresh,
  }
}

/**
 * Exposes authentication state from the Zustand store.
 */
export function useNativeHandlerAuthentication() {
  const authStore = useAuthStore()

  const getIsLoggedOut = useCallback(async () => {
    return !authStore.isAuthenticated()
  }, [authStore])

  return {
    getIsLoggedOut,
  }
}
